import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { BffAuthState } from '@iberiavo/web-shared'

/**
 * authStore — TotalVO.
 *
 * El store implementa la interfaz `BffAuthState` del paquete
 * `@iberiavo/web-shared` y anade los helpers propios que TVO necesita
 * (`login(user, token)`, `logout()`, `isAdmin()`, `getEffectiveRole()`).
 *
 *  - El hook `useBffCurrentUser` del paquete consume `markActive`/`clear`
 *    como cualquier otro portal BFF.
 *  - Las pages portadas de TC y componentes existentes pueden seguir usando
 *    `useAuthStore((s) => s.user)`, `s.login(user, _token)`,
 *    `s.logout()`, `s.isAdmin()`, `s.getEffectiveRole()` sin cambios.
 *
 * Decision deliberada: NO usamos `createBffAuthStore` directamente porque
 * el factory del paquete devuelve un store con shape `BffAuthState`
 * estricto y no permite anadir metodos propios. Aqui re-implementamos el
 * shape `BffAuthState` (compatible 1:1) + extras, usando zustand
 * directamente. El lock-step con el paquete se mantiene via `BffAuthState`
 * como contrato de tipo.
 *
 * El `token` que recibe `login()` se ignora — el modelo BFF no expone
 * tokens al cliente, la sesion vive en la cookie HttpOnly `bff_session`.
 * Se mantiene la firma `(user, token)` para no romper los call-sites
 * existentes (`useCurrentUser`, MobileNavigation, etc.).
 */

export interface AuthUser {
  id: string
  email: string
  role: string
  name?: string
  /**
   * Flag IberiaVO admin (paridad con TC/TTI 2026-04-29). Cuando es true el
   * usuario tiene privilegios de admin global aunque su `role` no sea
   * 'admin'. Persiste en BD (`users.is_iberiavo_admin`). En TVO la columna
   * todavia no existe (migracion pendiente); el backend devuelve `false`
   * por defecto via UserResponse hasta que se anada.
   */
  is_iberiavo_admin?: boolean
  [key: string]: unknown
}

interface AuthState extends BffAuthState<AuthUser> {
  // ── API propia de TVO sobre BFF ──
  /** Compat: el segundo argumento se ignora en BFF mode (cookie HttpOnly). */
  token: null
  login: (user: AuthUser, _token?: string) => void
  logout: () => void
  isAdmin: () => boolean
  /**
   * Rol efectivo (TC-compat): si el usuario tiene `simulated_role` o
   * `effective_role` en preferencias, prevalece sobre `role`. Lo usan las
   * paginas portadas de TC (FeedbackPage, FeedbackDetailPage) para detectar
   * "admin simulando comercial" sin llamadas async extra.
   */
  getEffectiveRole: () => string
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // ── BFF state ──
      user: null,
      isAuthenticated: false,
      markActive: (user) => set({ user, isAuthenticated: true }),
      clear: () => set({ user: null, isAuthenticated: false }),
      updateUser: (partial) => {
        const u = get().user
        if (!u) return
        set({ user: { ...u, ...partial } })
      },

      // ── Alias propios de TVO ──
      token: null,
      login: (user, _token) => set({ user, isAuthenticated: true }),
      logout: () => set({ user: null, isAuthenticated: false }),
      isAdmin: () => {
        const u = get().user
        if (!u) return false
        return u.role === 'admin' || u.is_iberiavo_admin === true
      },
      getEffectiveRole: () => {
        const u = get().user
        if (!u) return ''
        return (
          (u.effective_role as string | undefined) ||
          (u.simulated_role as string | undefined) ||
          u.role ||
          ''
        )
      },
    }),
    {
      // storeKey heredado — usuarios PRE no tienen que volver a loguearse.
      // El shape persistido cambia de `{ user, token }` a `{ user,
      // isAuthenticated }` pero el campo `user` se preserva. En el primer
      // reload tras el deploy `isAuthenticated` puede quedar `false` hasta
      // que `useBffCurrentUser` reciba el primer `/me` 200, momento en el
      // que `markActive` lo setea a true.
      name: 'totalvo-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)
